/**
 * Educator Community Page
 *
 * A warm bulletin-board style space for educators to share ideas,
 * ask questions, and support each other around SEL practice.
 *
 * Channels mirror the 7 Dira domains plus General and Admin Updates.
 * Admins can pin posts. Authors can delete their own posts/replies.
 */

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  BookOpen, LogOut, MessageCircle, Send, Trash2,
  Pin, PinOff, ChevronDown, ChevronUp, Users,
  Sparkles, Megaphone, Plus, X, GraduationCap,
  ThumbsUp, ThumbsDown, Pencil, Check, Search,
  Library, ExternalLink, Star
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Post {
  id: string;
  user_id: string;
  channel: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  author_name: string;
  author_school?: string;
  author_grade?: string;
  reply_count: number;
  upvotes: number;
  downvotes: number;
}

interface Reply {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  author_name: string;
}

interface Resource {
  id: string;
  user_id: string;
  title: string;
  type: "book" | "card_deck" | "tool" | "article" | "other";
  description: string;
  url?: string;
  author_name: string;
  is_featured: boolean;
  created_at: string;
}

const RESOURCE_TYPES: { value: Resource["type"]; label: string; emoji: string; color: string }[] = [
  { value: "book",      label: "Book",      emoji: "📚", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "card_deck", label: "Card Deck", emoji: "🃏", color: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "tool",      label: "Tool",      emoji: "🛠️", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "article",   label: "Article",   emoji: "📄", color: "bg-green-100 text-green-700 border-green-200" },
  { value: "other",     label: "Other",     emoji: "✨", color: "bg-gray-100 text-gray-700 border-gray-200" },
];

// ─── Channel Config ────────────────────────────────────────────────────────────

const CHANNELS = [
  { id: "admin_updates", label: "Admin Updates", icon: Megaphone,     color: "text-golden-600",  bg: "bg-golden-50",  border: "border-golden-200",  badge: "bg-golden-100 text-golden-700" },
  { id: "general",       label: "General",       icon: MessageCircle, color: "text-forest-600",  bg: "bg-forest-50",  border: "border-forest-200",  badge: "bg-forest-100 text-forest-700" },
  { id: "sel",           label: "SEL",           icon: Users,         color: "text-blue-600",    bg: "bg-blue-50",    border: "border-blue-200",    badge: "bg-blue-100 text-blue-700" },
  { id: "mattering",     label: "Mattering",     icon: Sparkles,      color: "text-violet-600",  bg: "bg-violet-50",  border: "border-violet-200",  badge: "bg-violet-100 text-violet-700" },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getInitials(name: string) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getChannelConfig(id: string) {
  return CHANNELS.find(c => c.id === id) || CHANNELS[0];
}

// ─── Post Card Component ───────────────────────────────────────────────────────

function PostCard({
  post, currentUser, token, isAdmin,
  onDelete, onPin, onReplyCountChange, onEdit, onVote
}: {
  post: Post;
  currentUser: any;
  token: string;
  isAdmin: boolean;
  onDelete: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onReplyCountChange: (id: string, count: number) => void;
  onEdit: (id: string, content: string) => void;
  onVote: (id: string, direction: "up" | "down") => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.content);
  const [savingEdit, setSavingEdit] = useState(false);
  const channel = getChannelConfig(post.channel);
  const Icon = channel.icon;

  const loadReplies = async () => {
    if (loadingReplies) return;
    setLoadingReplies(true);
    try {
      const res = await fetch(`/api/community/replies/${post.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReplies(data.replies || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingReplies(false); }
  };

  const handleExpand = () => {
    if (!expanded) loadReplies();
    setExpanded(p => !p);
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/community/replies", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ post_id: post.id, content: replyText.trim() })
      });
      if (res.ok) {
        setReplyText("");
        await loadReplies();
        onReplyCountChange(post.id, replies.length + 1);
      } else toast.error("Failed to post reply");
    } catch (e) { toast.error("Failed to post reply"); }
    finally { setSubmitting(false); }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      const res = await fetch(`/api/community/replies/${replyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setReplies(p => p.filter(r => r.id !== replyId));
        onReplyCountChange(post.id, Math.max(0, post.reply_count - 1));
      }
    } catch (e) { toast.error("Failed to delete reply"); }
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || editText.trim() === post.content) { setIsEditing(false); return; }
    setSavingEdit(true);
    try {
      const res = await fetch(`/api/community/posts/${post.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: editText.trim() })
      });
      if (res.ok) {
        onEdit(post.id, editText.trim());
        setIsEditing(false);
        toast.success("Post updated");
      } else toast.error("Failed to update post");
    } catch (e) { toast.error("Failed to update post"); }
    finally { setSavingEdit(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className={`border ${post.is_pinned ? `border-golden-300 bg-golden-50/30` : "border-forest-100"} hover:border-forest-200 transition-colors`}>
        <CardContent className="p-4">
          {/* Post header */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-forest-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-forest-700">{getInitials(post.author_name)}</span>
              </div>
              <div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-forest-900">{post.author_name}</span>
                  {post.author_grade && <span className="text-xs text-forest-400">{post.author_grade}</span>}
                  {post.author_school && <span className="text-xs text-forest-400">· {post.author_school}</span>}
                </div>
                <span className="text-xs text-forest-400">{timeAgo(post.created_at)}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {post.is_pinned && (
                <Badge className="bg-golden-100 text-golden-700 border-golden-200 text-xs gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </Badge>
              )}
              {isAdmin && (
                <button
                  onClick={() => onPin(post.id, !post.is_pinned)}
                  className="p-1 rounded text-forest-300 hover:text-golden-500 transition-colors"
                  title={post.is_pinned ? "Unpin" : "Pin post"}
                >
                  {post.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                </button>
              )}
              {(isAdmin || post.user_id === currentUser?.id) && (
                <button
                  onClick={() => onDelete(post.id)}
                  className="p-1 rounded text-forest-300 hover:text-coral-400 transition-colors"
                  title="Delete post"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Post content — editable if owner is editing */}
          {isEditing ? (
            <div className="mb-3 space-y-2">
              <Textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={3}
                className="text-sm border-forest-200 resize-none"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setEditText(post.content); }}>
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={handleSaveEdit} disabled={savingEdit} className="bg-forest-600 hover:bg-forest-700 text-white">
                  {savingEdit ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-forest-800 leading-relaxed mb-3 whitespace-pre-wrap">{post.content}</p>
          )}

          {/* Vote + reply row */}
          <div className="flex items-center gap-3">
            {/* Upvote */}
            <button
              onClick={() => onVote(post.id, "up")}
              className="flex items-center gap-1 text-xs text-forest-400 hover:text-forest-600 transition-colors"
              title="Upvote"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
              <span>{post.upvotes ?? 0}</span>
            </button>

            {/* Downvote */}
            <button
              onClick={() => onVote(post.id, "down")}
              className="flex items-center gap-1 text-xs text-forest-400 hover:text-coral-500 transition-colors"
              title="Downvote"
            >
              <ThumbsDown className="w-3.5 h-3.5" />
              <span>{post.downvotes ?? 0}</span>
            </button>

            {/* Replies */}
            <button
              onClick={handleExpand}
              className="flex items-center gap-1.5 text-xs text-forest-500 hover:text-forest-700 transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {post.reply_count > 0 ? `${post.reply_count} ${post.reply_count === 1 ? "reply" : "replies"}` : "Reply"}
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {/* Edit — author only */}
            {!isEditing && post.user_id === currentUser?.id && (
              <button
                onClick={() => { setIsEditing(true); setEditText(post.content); }}
                className="flex items-center gap-1 text-xs text-forest-400 hover:text-forest-600 transition-colors ml-auto"
                title="Edit post"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>

          {/* Replies section */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 pt-3 border-t border-forest-100 space-y-3">
                  {loadingReplies && (
                    <div className="text-xs text-forest-400 text-center py-2">Loading replies...</div>
                  )}

                  {replies.map(reply => (
                    <div key={reply.id} className="flex gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-forest-50 border border-forest-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-forest-600">{getInitials(reply.author_name)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-forest-800">{reply.author_name}</span>
                            <span className="text-xs text-forest-400">{timeAgo(reply.created_at)}</span>
                          </div>
                          {(isAdmin || reply.user_id === currentUser?.id) && (
                            <button
                              onClick={() => handleDeleteReply(reply.id)}
                              className="text-forest-300 hover:text-coral-400 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-forest-700 leading-relaxed mt-0.5 whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    </div>
                  ))}

                  {/* Reply input */}
                  <div className="flex gap-2 pt-1">
                    <div className="w-6 h-6 rounded-full bg-forest-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-xs font-bold text-white">{getInitials(currentUser?.name || "?")}</span>
                    </div>
                    <div className="flex-1 flex gap-2">
                      <Textarea
                        placeholder="Write a reply..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={2}
                        className="text-xs resize-none border-forest-200 flex-1"
                        onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleReply(); }}
                      />
                      <Button
                        onClick={handleReply}
                        disabled={submitting || !replyText.trim()}
                        size="sm"
                        className="bg-forest-600 hover:bg-forest-700 text-white self-end"
                      >
                        {submitting ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ─── Resource Card Component ──────────────────────────────────────────────────

function ResourceCard({
  resource, currentUser, token, isAdmin, onDelete, onFeature
}: {
  resource: Resource;
  currentUser: any;
  token: string;
  isAdmin: boolean;
  onDelete: (id: string) => void;
  onFeature: (id: string, featured: boolean) => void;
}) {
  const typeConfig = RESOURCE_TYPES.find(t => t.value === resource.type) || RESOURCE_TYPES[4];
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card className={`border ${resource.is_featured ? "border-golden-300 bg-golden-50/20" : "border-forest-100"} hover:border-forest-200 transition-colors`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
              <span className="text-2xl flex-shrink-0 mt-0.5">{typeConfig.emoji}</span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-forest-900 leading-snug">{resource.title}</h4>
                  {resource.is_featured && (
                    <Badge className="bg-golden-100 text-golden-700 border-golden-200 text-xs gap-1">
                      <Star className="w-3 h-3" /> Featured
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={`text-xs border ${typeConfig.color}`}>{typeConfig.label}</Badge>
                  <span className="text-xs text-forest-400">Shared by {resource.author_name}</span>
                  <span className="text-xs text-forest-400">· {timeAgo(resource.created_at)}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {isAdmin && (
                <button
                  onClick={() => onFeature(resource.id, !resource.is_featured)}
                  className="p-1 rounded text-forest-300 hover:text-golden-500 transition-colors"
                  title={resource.is_featured ? "Unfeature" : "Feature this resource"}
                >
                  <Star className="w-3.5 h-3.5" />
                </button>
              )}
              {(isAdmin || resource.user_id === currentUser?.id) && (
                <button
                  onClick={() => onDelete(resource.id)}
                  className="p-1 rounded text-forest-300 hover:text-coral-400 transition-colors"
                  title="Delete resource"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-forest-700 leading-relaxed mb-3">{resource.description}</p>
          {resource.url && (
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-forest-600 hover:text-forest-800 font-medium border border-forest-200 rounded-lg px-2.5 py-1 hover:bg-forest-50 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Visit Link
            </a>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function CommunityPage() {
  const { user, logout, token } = useAuth();
  const isAdmin = user?.role === "admin";

  const [activeView, setActiveView] = useState<"channels" | "resources">("channels");
  const [activeChannel, setActiveChannel] = useState("general");
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [newPostText, setNewPostText] = useState("");
  const [showNewPost, setShowNewPost] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // ── Resources state ──
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourceFilter, setResourceFilter] = useState<Resource["type"] | "all">("all");
  const [showResourceForm, setShowResourceForm] = useState(false);
  const [resourceForm, setResourceForm] = useState({ title: "", type: "book" as Resource["type"], description: "", url: "" });
  const [submittingResource, setSubmittingResource] = useState(false);
  const [resourceSearch, setResourceSearch] = useState("");

  const channel = getChannelConfig(activeChannel);
  const ChannelIcon = channel.icon;

  useEffect(() => {
    if (token) loadPosts(activeChannel);
  }, [activeChannel, token]);

  useEffect(() => {
    if (token && activeView === "resources") loadResources();
  }, [activeView, token]);

  const loadPosts = async (ch: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/community/posts/${ch}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      } else {
        toast.error("Failed to load posts");
      }
    } catch (e) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!newPostText.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/community/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel: activeChannel, content: newPostText.trim() })
      });
      if (res.ok) {
        setNewPostText("");
        setShowNewPost(false);
        toast.success("Posted!");
        await loadPosts(activeChannel);
      } else {
        toast.error("Failed to post");
      }
    } catch (e) {
      toast.error("Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (postId: string) => {
    try {
      const res = await fetch(`/api/community/posts/${postId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPosts(p => p.filter(post => post.id !== postId));
        toast.success("Post deleted");
      }
    } catch (e) { toast.error("Failed to delete"); }
  };

  const handlePin = async (postId: string, isPinned: boolean) => {
    try {
      const res = await fetch(`/api/community/posts/${postId}/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_pinned: isPinned })
      });
      if (res.ok) {
        setPosts(p => p.map(post => post.id === postId ? { ...post, is_pinned: isPinned } : post));
      }
    } catch (e) { toast.error("Failed to pin post"); }
  };

  const handleReplyCountChange = (postId: string, count: number) => {
    setPosts(p => p.map(post => post.id === postId ? { ...post, reply_count: count } : post));
  };

  const handleEdit = (postId: string, content: string) => {
    setPosts(p => p.map(post => post.id === postId ? { ...post, content } : post));
  };

  const handleVote = (postId: string, direction: "up" | "down") => {
    setPosts(p => p.map(post => post.id === postId ? {
      ...post,
      upvotes: direction === "up" ? (post.upvotes ?? 0) + 1 : (post.upvotes ?? 0),
      downvotes: direction === "down" ? (post.downvotes ?? 0) + 1 : (post.downvotes ?? 0),
    } : post));
  };

  const loadResources = async () => {
    setResourcesLoading(true);
    try {
      const res = await fetch("/api/community/resources", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResources(data.resources || []);
      } else toast.error("Failed to load resources");
    } catch (e) { toast.error("Failed to load resources"); }
    finally { setResourcesLoading(false); }
  };

  const handleSubmitResource = async () => {
    if (!resourceForm.title.trim() || !resourceForm.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSubmittingResource(true);
    try {
      const res = await fetch("/api/community/resources", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(resourceForm)
      });
      if (res.ok) {
        toast.success("Resource shared!");
        setShowResourceForm(false);
        setResourceForm({ title: "", type: "book", description: "", url: "" });
        await loadResources();
      } else toast.error("Failed to share resource");
    } catch (e) { toast.error("Failed to share resource"); }
    finally { setSubmittingResource(false); }
  };

  const handleDeleteResource = async (id: string) => {
    try {
      const res = await fetch(`/api/community/resources/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setResources(p => p.filter(r => r.id !== id));
        toast.success("Resource removed");
      }
    } catch (e) { toast.error("Failed to delete resource"); }
  };

  const handleFeatureResource = async (id: string, featured: boolean) => {
    try {
      const res = await fetch(`/api/community/resources/${id}/feature`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_featured: featured })
      });
      if (res.ok) {
        setResources(p => p.map(r => r.id === id ? { ...r, is_featured: featured } : r));
      }
    } catch (e) { toast.error("Failed to update resource"); }
  };

  const filteredResources = resources.filter(r => {
    const matchesType = resourceFilter === "all" || r.type === resourceFilter;
    const matchesSearch = !resourceSearch.trim() ||
      r.title.toLowerCase().includes(resourceSearch.toLowerCase()) ||
      r.description.toLowerCase().includes(resourceSearch.toLowerCase()) ||
      r.author_name.toLowerCase().includes(resourceSearch.toLowerCase());
    return matchesType && matchesSearch;
  });

  const filteredPosts = searchQuery.trim()
    ? posts.filter(p => p.content.toLowerCase().includes(searchQuery.toLowerCase()) || p.author_name.toLowerCase().includes(searchQuery.toLowerCase()))
    : posts;

  return (
    <div className="min-h-screen bg-cream-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-forest-100 sticky top-0 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-forest-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-forest-900 leading-tight">Educator Community</h1>
              <p className="text-xs text-forest-500">Where the Brave Things Grow</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild className="text-forest-600 hover:bg-forest-50">
              <Link to="/library">
                <BookOpen className="w-4 h-4 mr-1.5" />
                My Library
              </Link>
            </Button>
            {(user?.role === "educator" || isAdmin) && (
              <Button variant="ghost" size="sm" asChild className="text-golden-600 hover:bg-golden-50">
                <Link to="/educator">
                  <GraduationCap className="w-4 h-4 mr-1.5" />
                  Dashboard
                </Link>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => logout()} className="text-coral-600 hover:bg-coral-50">
              <LogOut className="w-4 h-4 mr-1.5" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 max-w-7xl mx-auto w-full">
        {/* Sidebar — channels */}
        <aside className="w-56 bg-white border-r border-forest-100 flex-shrink-0 sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto">
          <nav className="p-3 space-y-0.5">
            <div className="px-3 py-2 mb-1">
              <p className="text-xs font-semibold text-forest-400 uppercase tracking-wider">Channels</p>
            </div>
            {CHANNELS.map(ch => {
              const Icon = ch.icon;
              const isActive = activeView === "channels" && activeChannel === ch.id;
              return (
                <button
                  key={ch.id}
                  onClick={() => { setActiveView("channels"); setActiveChannel(ch.id); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                    isActive ? "bg-forest-600 text-white" : "text-forest-700 hover:bg-forest-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : ch.color}`} />
                  <span className={`text-sm font-medium truncate ${isActive ? "text-white" : "text-forest-800"}`}>
                    {ch.label}
                  </span>
                </button>
              );
            })}

            {/* Resources tab */}
            <div className="px-3 pt-4 pb-1 mt-2 border-t border-forest-100">
              <p className="text-xs font-semibold text-forest-400 uppercase tracking-wider">Library</p>
            </div>
            <button
              onClick={() => setActiveView("resources")}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-all ${
                activeView === "resources" ? "bg-forest-600 text-white" : "text-forest-700 hover:bg-forest-50"
              }`}
            >
              <Library className={`w-4 h-4 flex-shrink-0 ${activeView === "resources" ? "text-white" : "text-forest-600"}`} />
              <span className={`text-sm font-medium ${activeView === "resources" ? "text-white" : "text-forest-800"}`}>
                SEL Resources
              </span>
            </button>
          </nav>
        </aside>

        {/* Main feed */}
        <main className="flex-1 p-6 max-w-2xl">

          {/* ── RESOURCES VIEW ── */}
          {activeView === "resources" && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-forest-50 border border-forest-200 flex items-center justify-center">
                    <Library className="w-5 h-5 text-forest-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-forest-900">SEL Resource Library</h2>
                    <p className="text-xs text-forest-400">{resources.length} {resources.length === 1 ? "resource" : "resources"} shared</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowResourceForm(p => !p)}
                  className="bg-forest-600 hover:bg-forest-700 text-white gap-2"
                  size="sm"
                >
                  {showResourceForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {showResourceForm ? "Cancel" : "Share Resource"}
                </Button>
              </div>

              {/* Submit form */}
              <AnimatePresence>
                {showResourceForm && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                    <Card className="border-forest-200 shadow-sm">
                      <CardContent className="p-4 space-y-3">
                        <p className="text-sm font-medium text-forest-800">Share an SEL resource with the community</p>
                        <Input
                          placeholder="Title *"
                          value={resourceForm.title}
                          onChange={e => setResourceForm(p => ({ ...p, title: e.target.value }))}
                          className="border-forest-200 text-sm"
                        />
                        <div className="flex gap-2 flex-wrap">
                          {RESOURCE_TYPES.map(t => (
                            <button
                              key={t.value}
                              onClick={() => setResourceForm(p => ({ ...p, type: t.value }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                resourceForm.type === t.value
                                  ? "bg-forest-600 text-white border-forest-600"
                                  : "border-forest-200 text-forest-600 hover:bg-forest-50"
                              }`}
                            >
                              {t.emoji} {t.label}
                            </button>
                          ))}
                        </div>
                        <textarea
                          placeholder="Description — what is it, why is it useful? *"
                          value={resourceForm.description}
                          onChange={e => setResourceForm(p => ({ ...p, description: e.target.value }))}
                          rows={3}
                          className="w-full text-sm border border-forest-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-forest-300"
                        />
                        <Input
                          placeholder="Link / URL (optional)"
                          value={resourceForm.url}
                          onChange={e => setResourceForm(p => ({ ...p, url: e.target.value }))}
                          className="border-forest-200 text-sm"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setShowResourceForm(false); setResourceForm({ title: "", type: "book", description: "", url: "" }); }}>
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSubmitResource}
                            disabled={submittingResource || !resourceForm.title.trim() || !resourceForm.description.trim()}
                            className="bg-forest-600 hover:bg-forest-700 text-white gap-2"
                          >
                            {submittingResource
                              ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              : <Send className="w-3.5 h-3.5" />
                            }
                            Share
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Search + filter */}
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-400" />
                  <Input
                    placeholder="Search resources..."
                    value={resourceSearch}
                    onChange={e => setResourceSearch(e.target.value)}
                    className="pl-9 border-forest-200 bg-white text-sm"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setResourceFilter("all")}
                    className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                      resourceFilter === "all" ? "bg-forest-600 text-white border-forest-600" : "border-forest-200 text-forest-600 hover:bg-forest-50"
                    }`}
                  >
                    All
                  </button>
                  {RESOURCE_TYPES.map(t => (
                    <button
                      key={t.value}
                      onClick={() => setResourceFilter(t.value)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium border transition-colors ${
                        resourceFilter === t.value ? "bg-forest-600 text-white border-forest-600" : "border-forest-200 text-forest-600 hover:bg-forest-50"
                      }`}
                    >
                      {t.emoji} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resources list */}
              {resourcesLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-forest-200 border-t-forest-600 rounded-full animate-spin" />
                </div>
              )}

              {!resourcesLoading && filteredResources.length === 0 && (
                <div className="text-center py-16 space-y-3">
                  <div className="w-14 h-14 bg-forest-50 rounded-2xl flex items-center justify-center mx-auto border border-forest-200">
                    <Library className="w-7 h-7 text-forest-400" />
                  </div>
                  <p className="text-forest-600 font-medium">No resources yet</p>
                  <p className="text-forest-400 text-sm">Be the first to share an SEL resource</p>
                </div>
              )}

              <div className="space-y-3">
                {filteredResources.map(resource => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    currentUser={user}
                    token={token || ""}
                    isAdmin={isAdmin}
                    onDelete={handleDeleteResource}
                    onFeature={handleFeatureResource}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── CHANNELS VIEW ── */}
          {activeView === "channels" && (<>
          {/* Channel header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-xl ${channel.bg} border ${channel.border} flex items-center justify-center`}>
                <ChannelIcon className={`w-5 h-5 ${channel.color}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-forest-900">{channel.label}</h2>
                <p className="text-xs text-forest-400">
                  {posts.length} {posts.length === 1 ? "post" : "posts"}
                </p>
              </div>
            </div>

            {/* Only admins can post in Admin Updates; everyone else can post in other channels */}
            {(activeChannel !== "admin_updates" || isAdmin) && (
              <Button
                onClick={() => setShowNewPost(p => !p)}
                className="bg-forest-600 hover:bg-forest-700 text-white gap-2"
                size="sm"
              >
                {showNewPost ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showNewPost ? "Cancel" : "New Post"}
              </Button>
            )}
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-400" />
            <Input
              placeholder="Search posts..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 border-forest-200 bg-white text-sm"
            />
          </div>

          {/* New post form */}
          <AnimatePresence>
            {showNewPost && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-4"
              >
                <Card className="border-forest-200 shadow-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-forest-600 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{getInitials(user?.name || "?")}</span>
                      </div>
                      <span className="text-sm font-medium text-forest-800">{user?.name}</span>
                    </div>
                    <Textarea
                      placeholder={`Share something with the community in ${channel.label}...`}
                      value={newPostText}
                      onChange={e => setNewPostText(e.target.value)}
                      rows={4}
                      className="border-forest-200 resize-none"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setShowNewPost(false); setNewPostText(""); }}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handlePost}
                        disabled={submitting || !newPostText.trim()}
                        className="bg-forest-600 hover:bg-forest-700 text-white gap-2"
                      >
                        {submitting
                          ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Send className="w-3.5 h-3.5" />
                        }
                        Post
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Posts feed */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-forest-200 border-t-forest-600 rounded-full animate-spin" />
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-16 space-y-3">
              <div className={`w-14 h-14 ${channel.bg} rounded-2xl flex items-center justify-center mx-auto border ${channel.border}`}>
                <ChannelIcon className={`w-7 h-7 ${channel.color}`} />
              </div>
              <p className="text-forest-600 font-medium">No posts yet in {channel.label}</p>
              <p className="text-forest-400 text-sm">Be the first to share something</p>
            </div>
          )}

          <div className="space-y-3">
            {filteredPosts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={user}
                token={token || ""}
                isAdmin={isAdmin}
                onDelete={handleDelete}
                onPin={handlePin}
                onReplyCountChange={handleReplyCountChange}
                onEdit={handleEdit}
                onVote={handleVote}
              />
            ))}
          </div>
          </>)}
        </main>
      </div>
    </div>
  );
}
